import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * The three-step "How Innovate works" explainer (task 526).
 *
 * Lifted verbatim out of `HomePage` so the same strip can appear at every
 * moment of first contact — the adaptive Home invitation state, the first-run
 * AI Services compose view, and the first-run Dashboard checklist (task 525).
 * Copy is unchanged from the original HomePage version.
 *
 * `testId` keeps HomePage's original `home-how-it-works` hook intact; other
 * surfaces get the default. `onDismiss`, when provided, renders a dismiss
 * control — surfaces that persist the choice pass a handler backed by the
 * shared UI-preference dismissal store (`useDismissiblePanel`).
 */

/** Preference key for the per-User dismissal of the strip. */
export const HOW_IT_WORKS_PANEL_KEY = 'how-it-works';

const STEPS = [
  { title: 'Describe it', body: 'Tell us what you want your AI to do, in your own words.' },
  { title: 'We compose it', body: 'Innovate assembles a working AI Service behind the scenes.' },
  { title: 'Use it anywhere', body: 'Call it from outside the app, in any application you build.' },
];

interface HowItWorksStripProps {
  /** Test hook for the wrapper. Defaults to `how-it-works-strip`. */
  testId?: string;
  /** When provided, a dismiss button is rendered and calls this on click. */
  onDismiss?: () => void;
}

export default function HowItWorksStrip({
  testId = 'how-it-works-strip',
  onDismiss,
}: HowItWorksStripProps) {
  const grid = (
    <div className="grid gap-4 sm:grid-cols-3" data-testid={testId}>
      {STEPS.map((step, i) => (
        <div
          key={step.title}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5"
        >
          <div className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-2">
            Step {i + 1}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">{step.title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{step.body}</p>
        </div>
      ))}
    </div>
  );

  if (!onDismiss) return grid;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onDismiss}
        data-testid="how-it-works-dismiss"
        aria-label="Dismiss how it works"
        className="absolute -top-2 right-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
      {grid}
    </div>
  );
}
